// client/src/api/auth/authApi.ts
//
// Tutti i domain-file devono usare boundApiFetch — mai apiFetch direttamente.
// boundApiFetch inietta getStore automaticamente e gestisce l'impersonation.

import { boundApiFetch } from "../core/boundApiFetch";
import type { User } from "@/types";

// =====================
// AUTH
// =====================

/** Login completato: sessione emessa (user + token). */
export interface LoginSuccess {
  user: User;
  accessToken: string;
}

/**
 * OTT 2026 — 2FA: il login con password non emette la sessione finché non
 * è confermato il codice TOTP. `mfa_token` prova che la password è corretta.
 */
export interface MfaChallenge {
  mfa_required: true;
  mfa_token: string;
}

/** OTT 2026 — Verifica email obbligatoria: la sessione arriva dopo il codice. */
export interface EmailVerificationPending {
  email_verification_required: true;
  email: string;
  message?: string;
}

export type LoginResponse = LoginSuccess | MfaChallenge;
export type RegisterResponse = LoginSuccess | EmailVerificationPending;

/** Type guard: la risposta del login richiede la verifica del 2FA. */
export function isMfaChallenge(d: LoginResponse): d is MfaChallenge {
  return "mfa_required" in d && d.mfa_required === true;
}

/** Type guard: la registrazione richiede la conferma dell'email. */
export function isEmailVerificationPending(
  d: RegisterResponse,
): d is EmailVerificationPending {
  return (
    "email_verification_required" in d && d.email_verification_required === true
  );
}

export async function register(
  email: string,
  password: string,
  phone: string,
  nickname?: string,
  referralCode?: string,
  privacyAccepted?: boolean,
  honeypot?: string,
) {
  // TAPPE (Ott 2026): il codice del locale arriva da `?tappa=` (QR al bancone).
  // Lo leggiamo qui così il form di registrazione non deve cambiare firma.
  const tappaCode =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
          .get("tappa")
          ?.trim()
          .toUpperCase() || undefined
      : undefined;
  return boundApiFetch<RegisterResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      phone,
      nickname,
      referral_code: referralCode || undefined,
      privacy_accepted: privacyAccepted || undefined,
      // ANTI-BOT (Ott 2026): campo esca del form (invisibile alle persone).
      // Se un bot lo compila il server scarta la registrazione.
      website_url: honeypot && honeypot.length > 0 ? honeypot : undefined,
      tappa_code: tappaCode && tappaCode.length >= 6 ? tappaCode : undefined,
    }),
  });
}

export async function login(identifier: string, password: string) {
  return boundApiFetch<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password }),
  });
}

/** OTT 2026 — 2FA: completa il login confermando il codice TOTP. */
export async function verifyMfaLogin(mfaToken: string, code: string) {
  return boundApiFetch<LoginSuccess>("/api/auth/mfa/verify-login", {
    method: "POST",
    body: JSON.stringify({ mfa_token: mfaToken, code }),
  });
}

/**
 * OTT 2026 — MFA-RECOVERY: invia all'email dell'account un codice a 6 cifre
 * per disattivare il 2FA quando l'utente ha perso l'accesso all'app
 * Authenticator. Risponde con l'email mascherata di destinazione.
 */
export async function requestMfaReset(mfaToken: string) {
  return boundApiFetch<{
    sent: true;
    email: string;
    telegram_sent?: boolean;
    message: string;
  }>("/api/auth/mfa/reset-request", {
    method: "POST",
    body: JSON.stringify({ mfa_token: mfaToken }),
  });
}

/** OTT 2026 — MFA-RECOVERY: conferma il codice e disattiva il 2FA. */
export async function confirmMfaReset(mfaToken: string, code: string) {
  return boundApiFetch<{ success: true; message: string }>(
    "/api/auth/mfa/reset-confirm",
    {
      method: "POST",
      body: JSON.stringify({ mfa_token: mfaToken, code }),
    },
  );
}

/**
 * OTT 2026 — SESSIONE UNICA (anti-condivisione credenziali): l'utente ha
 * confermato di voler disconnettere l'altra sessione attiva e completare
 * l'accesso su questo device.
 */
export async function resolveSessionConflict(conflictToken: string) {
  return boundApiFetch<LoginSuccess>("/api/auth/session-conflict", {
    method: "POST",
    body: JSON.stringify({
      conflict_token: conflictToken,
      action: "revoke_others",
    }),
  });
}

// =====================
// SBLOCCO BIOMETRICO (Ott 2026)
// =====================

export interface BiometricDeviceInfo {
  device_id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
}

/**
 * Registra questo device per lo sblocco biometrico. Il `device_secret`
 * restituito è valido UNA SOLA VOLTA: va custodito nel secure storage.
 */
export async function enrollBiometricDevice(deviceId: string, label?: string) {
  return boundApiFetch<{
    device_id: string;
    device_secret: string;
    message: string;
  }>("/api/auth/biometric/enroll", {
    method: "POST",
    body: JSON.stringify({ device_id: deviceId, label }),
  });
}

/** Scambia il secret del device con una nuova sessione (pre-login). */
export async function biometricRefresh(deviceId: string, deviceSecret: string) {
  return boundApiFetch<LoginSuccess>("/api/auth/biometric/refresh", {
    method: "POST",
    body: JSON.stringify({ device_id: deviceId, device_secret: deviceSecret }),
  });
}

/** Revoca il device corrente (disattiva lo sblocco biometrico). */
export async function revokeBiometricDevice(deviceId: string) {
  return boundApiFetch<{ revoked: true }>("/api/auth/biometric/revoke", {
    method: "POST",
    body: JSON.stringify({ device_id: deviceId }),
  });
}

/** Elenco dei device con sblocco biometrico attivo. */
export async function listBiometricDevices() {
  return boundApiFetch<{ devices: BiometricDeviceInfo[] }>(
    "/api/auth/biometric/devices",
  );
}

/** OTT 2026 — Verifica email: conferma il codice e riceve la sessione. */
export async function verifyEmail(email: string, code: string) {
  return boundApiFetch<LoginSuccess>("/api/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ email, code }),
  });
}

/** OTT 2026 — Verifica email: reinvia il codice (risposta generica). */
export async function resendVerificationCode(email: string) {
  return boundApiFetch<{ success: true; message: string }>(
    "/api/auth/resend-verification",
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  );
}

export async function loginWithGoogle(credential: string) {
  return boundApiFetch<{ user: User; accessToken: string }>(
    "/api/auth/google",
    {
      method: "POST",
      body: JSON.stringify({ credential }),
    },
  );
}

/**
 * Sign in with Apple (1 Nov 2026). L'`identityToken` arriva dal plugin nativo
 * (@capgo/capacitor-social-login → ASAuthorizationAppleIDProvider); il server
 * verifica firma, issuer e audience prima di aprire la sessione.
 */
export async function loginWithApple(
  identityToken: string,
  names?: { firstName?: string | null; lastName?: string | null },
) {
  return boundApiFetch<{ user: User; accessToken: string }>("/api/auth/apple", {
    method: "POST",
    body: JSON.stringify({
      identityToken,
      firstName: names?.firstName ?? null,
      lastName: names?.lastName ?? null,
    }),
  });
}

export async function logoutApi(_refreshToken?: string) {
  return boundApiFetch("/api/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function me() {
  return boundApiFetch<{ user: User }>("/api/auth/me");
}

export async function requestPasswordReset(email: string) {
  return boundApiFetch<{ success: true; message: string }>(
    "/api/auth/password-reset-request",
    {
      method: "POST",
      body: JSON.stringify({ email }),
    },
  );
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
  email: string,
) {
  return boundApiFetch<{ success: true; message: string }>(
    "/api/auth/password-reset-confirm",
    {
      method: "POST",
      body: JSON.stringify({ token, newPassword, email }),
    },
  );
}

// ─── Profilo Fiscale ─────────────────────────────────────────────────────

export interface FiscalProfilePayload {
  first_name?: string | null;
  last_name?: string | null;
  codice_fiscale?: string | null;
  phone?: string | null;
  nickname?: string | null;
  phone_visible?: boolean | null;
  address_street?: string | null;
  address_civic?: string | null;
  address_cap?: string | null;
  address_city?: string | null;
  address_province?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  bike_type?: string | null;
  experience_level?: string | null;
  bio?: string | null;
  profile_visibility?: string | null;
  whatsapp_opt_in?: boolean | null;
}

export interface ProfileCheckResponse {
  complete: boolean;
  missing: string[];
  requiredFields: Record<string, boolean>;
}

export async function updateFiscalProfile(payload: FiscalProfilePayload) {
  return boundApiFetch<{ user: User; message: string }>(
    "/api/auth/me/profile",
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
}

export async function checkProfileComplete() {
  return boundApiFetch<ProfileCheckResponse>("/api/auth/me/profile/check");
}
